// Sharing out a hoard's coins and gems (qa-runs 1156): under a rolled hoard and on each held one, the DM chooses how —
// to one person, by percentage (two or more, adding up to 100 before going on), evenly among the whole group (every
// character in the campaign's party, at the table or not, each untickable), or hold it under a name. Then a preview,
// person by person: the coins by kind and the gems (each to one character, or held), every amount changeable. Nothing
// is given until Confirm; whatever is not given stays held. The split itself is `splitCoins` from vtt-shared, the
// same one the server checks against (`fitsHoard`), so what is shown is what is given.
import { useEffect, useMemo, useState } from 'react';
import { COIN_LADDER, fitsHoard, percentagesValid, splitCoins, describeShare } from '@dragons-table/vtt-shared';
import { useShell } from '../context';
import { useOptionalTableState } from '../../table/TableState';
import { listTableCharacters } from '../../characters';
import { giveHoard, holdHoard, type CoinCounts, type GiveOutcome, type HeldHoard, type HoardContents, type HoardGem } from '../../loot';
import { BTN } from '../ui';

export type ShareMode = 'one' | 'percent' | 'even' | 'hold';
export const SHARE_MODES: ReadonlyArray<{ id: ShareMode; label: string }> = [
  { id: 'one', label: 'To one person' }, { id: 'percent', label: 'By percentage' }, { id: 'even', label: 'Evenly among the whole group' }, { id: 'hold', label: 'Hold it' },
];
const COINS = COIN_LADDER.map((c) => c.coin);
const btn = `${BTN} shrink-0`;
const box = 'w-16 rounded border border-stone-700 bg-stone-900 px-1 py-0.5 text-xs';
type Person = { id: string; name: string };
type Row = { id: string; name: string; coins: Record<string, string> };

/** "1,200 gp, 30 sp" — a hoard's coins as a line. */
export const coinWords = (c: CoinCounts) => COINS.filter((k) => (c[k] ?? 0) > 0).map((k) => `${c[k]!.toLocaleString()} ${k}`).join(', ');
const whole = (s: string) => (/^\d{1,8}$/.test(s.trim()) ? Number(s.trim()) : s.trim() === '' ? 0 : NaN);

export function HoardShare({ source, held, onDone }: {
  /** What is being shared: a fresh roll's coins, gems and the items not yet given, or a held hoard. */
  source: HoardContents;
  held?: HeldHoard;
  onDone: (outcome: { said: string; held: HeldHoard | null; heldBefore?: string }) => void;
}) {
  const { campaignId } = useShell();
  const sceneId = useOptionalTableState()?.scene?.id ?? null;
  const [party, setParty] = useState<Person[] | null>(null);
  const [mode, setMode] = useState<ShareMode | null>(null);
  const [one, setOne] = useState('');
  const [percent, setPercent] = useState<Record<string, string>>({});
  const [left, setLeft] = useState<Set<string>>(new Set());
  const [restTo, setRestTo] = useState('');
  const [name, setName] = useState('');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [gemTo, setGemTo] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    void listTableCharacters(campaignId, sceneId).catch(() => null).then((list) => { if (live) setParty((list ?? []).map((c) => ({ id: c.id, name: c.name }))); });
    return () => { live = false; };
  }, [campaignId, sceneId]);

  // Who takes part, with their weights, in the mode chosen.
  const chosen = useMemo((): Array<{ id: string; weight: number }> => {
    if (!party) return [];
    if (mode === 'one') return one ? [{ id: one, weight: 1 }] : [];
    if (mode === 'even') return party.filter((p) => !left.has(p.id)).map((p) => ({ id: p.id, weight: 1 }));
    if (mode === 'percent') return Object.entries(percent).map(([id, v]) => ({ id, weight: Number(v) }));
    return [];
  }, [party, mode, one, left, percent]);
  const percentOk = mode !== 'percent' || percentagesValid(Object.entries(percent).map(([id, v]) => ({ id, percent: /^\d{1,3}$/.test(v) ? Number(v) : NaN })));
  const canGoOn = mode !== null && mode !== 'hold' && chosen.some((c) => c.weight > 0) && percentOk;
  const nameOf = (id: string) => party?.find((p) => p.id === id)?.name ?? '';

  const goOn = () => {
    const by = chosen.filter((c) => c.weight > 0);
    const rest = by.some((c) => c.id === restTo) ? restTo : by[0]!.id;
    const split = splitCoins(source.coins, by, rest);
    setRows(by.map((c) => ({ id: c.id, name: nameOf(c.id), coins: Object.fromEntries(COINS.map((k) => [k, String(split[c.id]?.[k] ?? 0)])) })));
    // Gems are given whole: all to the one person; otherwise held until the DM picks who.
    setGemTo(source.gems.map(() => (mode === 'one' ? by[0]!.id : '')));
    setError('');
  };
  const shares = useMemo(() => (rows ?? []).map((r) => ({
    characterId: r.id,
    coins: Object.fromEntries(COINS.map((k) => [k, whole(r.coins[k] ?? '')]).filter(([, n]) => (n as number) !== 0)) as CoinCounts,
    gems: source.gems.filter((_, i) => gemTo[i] === r.id),
  })), [rows, gemTo, source.gems]);
  const badNumber = shares.some((s) => Object.values(s.coins).some((n) => !Number.isFinite(n)));
  const fits = !badNumber && fitsHoard(source.coins, shares.map((s) => s.coins));
  const heldCoins = useMemo(() => {
    if (!fits) return 0;
    // The coins not given, as the server will hold them.
    const total = (c: CoinCounts) => COIN_LADDER.reduce((n, { coin, cp }) => n + (c[coin] ?? 0) * cp, 0);
    return total(source.coins) - shares.reduce((n, s) => n + total(s.coins), 0);
  }, [fits, shares, source.coins]);

  const confirm = async () => {
    setBusy(true); setError('');
    const ask = held ? { hoardId: held.id } : { hoard: source };
    const got = await giveHoard(campaignId, ask, shares.filter((s) => Object.keys(s.coins).length || s.gems.length)).catch(() => ({ kind: 'error' as const }));
    setBusy(false);
    if (got.kind !== 'ok') { setError(got.kind === 'refused' ? got.reason : "The give didn't go through. Try again in a moment."); return; }
    onDone({ said: (got as GiveOutcome).said, held: (got as GiveOutcome).held, ...(held ? { heldBefore: held.id } : {}) });
  };
  const hold = async () => {
    setBusy(true); setError('');
    const got = await holdHoard(campaignId, name.trim(), source).catch(() => ({ kind: 'error' as const }));
    setBusy(false);
    if (got.kind !== 'ok') { setError(got.kind === 'refused' ? got.reason : "The hoard couldn't be held just now. Try again in a moment."); return; }
    onDone({ said: `Holding ${got.hoard.name}.`, held: got.hoard });
  };

  const modes = SHARE_MODES.filter((m) => !(held && m.id === 'hold'));
  return (
    <div className="mt-1 space-y-2 rounded border border-stone-700 p-2" data-testid="hoard-share">
      {rows === null ? (
        <>
          <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="What happens to the coins and gems">
            {modes.map((m) => (
              <button key={m.id} type="button" role="radio" aria-checked={mode === m.id} data-testid={`hoard-mode-${m.id}`}
                className={`rounded px-2 py-0.5 text-xs ${mode === m.id ? 'bg-stone-700 text-stone-100' : 'border border-stone-700 text-stone-300 hover:bg-stone-800'}`}
                onClick={() => { setMode(m.id); setError(''); }}>{m.label}</button>
            ))}
          </div>
          {mode && mode !== 'hold' && party === null && <p role="status" className="text-xs text-stone-400">Reading the party…</p>}
          {mode && mode !== 'hold' && party?.length === 0 && <p className="text-xs text-stone-400">The campaign's party has no characters yet.</p>}
          {mode === 'one' && party && party.length > 0 && (
            <label className="block text-xs text-stone-400">To
              <select className={`${box} ml-1 w-auto`} value={one} data-testid="hoard-one" onChange={(e) => setOne(e.target.value)}>
                <option value="">Pick a character</option>
                {party.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          )}
          {mode === 'percent' && party && (
            <ul className="space-y-1">
              {party.map((p) => (
                <li key={p.id} className="flex items-center gap-2 text-xs">
                  <label className="flex min-w-0 flex-1 items-center gap-1 text-stone-200">
                    <input type="checkbox" checked={p.id in percent} data-testid={`hoard-pick-${p.id}`}
                      onChange={(e) => setPercent((was) => { const n = { ...was }; if (e.target.checked) n[p.id] = ''; else delete n[p.id]; return n; })} />
                    <span className="truncate">{p.name}</span>
                  </label>
                  {p.id in percent && (
                    <label className="text-stone-400"><input className={`${box} w-12`} inputMode="numeric" aria-label={`${p.name}'s percentage`} data-testid={`hoard-percent-${p.id}`}
                      value={percent[p.id]} onChange={(e) => setPercent((was) => ({ ...was, [p.id]: e.target.value }))} /> %</label>
                  )}
                </li>
              ))}
              {!percentOk && Object.keys(percent).length > 0 && <li className="text-xs text-amber-300" data-testid="hoard-percent-total">Pick two or more, adding up to 100%.</li>}
            </ul>
          )}
          {mode === 'even' && party && (
            <ul className="space-y-1">
              {party.map((p) => (
                <li key={p.id}>
                  <label className="flex items-center gap-1 text-xs text-stone-200">
                    <input type="checkbox" checked={!left.has(p.id)} data-testid={`hoard-even-${p.id}`}
                      onChange={(e) => setLeft((was) => { const n = new Set(was); if (e.target.checked) n.delete(p.id); else n.add(p.id); return n; })} />
                    {p.name}
                  </label>
                </li>
              ))}
            </ul>
          )}
          {mode && mode !== 'hold' && mode !== 'one' && chosen.filter((c) => c.weight > 0).length > 1 && (
            <label className="block text-xs text-stone-400">Copper left over to
              <select className={`${box} ml-1 w-auto`} value={restTo} data-testid="hoard-rest" onChange={(e) => setRestTo(e.target.value)}>
                {chosen.filter((c) => c.weight > 0).map((c) => <option key={c.id} value={c.id}>{nameOf(c.id)}</option>)}
              </select>
            </label>
          )}
          {mode === 'hold' && (
            <div className="flex flex-wrap items-center gap-2">
              <input className={`${box} w-48 max-w-full`} value={name} maxLength={80} placeholder="A name, e.g. Goblin cave" aria-label="The held hoard's name" data-testid="hoard-hold-name" onChange={(e) => setName(e.target.value)} />
              <button type="button" className={btn} disabled={busy || !name.trim()} data-testid="hoard-hold" onClick={() => void hold()}>Hold it</button>
            </div>
          )}
          {mode && mode !== 'hold' && <button type="button" className={btn} disabled={!canGoOn} data-testid="hoard-go-on" onClick={goOn}>Go on</button>}
        </>
      ) : (
        <>
          <p className="text-xs text-stone-400">Who gets what. Change any amount; nothing is given until you confirm.</p>
          <ul className="space-y-2" data-testid="hoard-preview">
            {rows.map((r, n) => (
              <li key={r.id} className="rounded border border-stone-800 p-1" data-testid={`hoard-row-${r.id}`}>
                <p className="text-xs font-medium text-stone-100">{r.name}</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {COINS.map((k) => (
                    <label key={k} className="text-xs text-stone-400">
                      <input className={`${box} w-20`} inputMode="numeric" aria-label={`${r.name}'s ${k}`} data-testid={`hoard-coin-${r.id}-${k}`} value={r.coins[k] ?? '0'}
                        onChange={(e) => setRows((was) => was!.map((x, i) => (i === n ? { ...x, coins: { ...x.coins, [k]: e.target.value } } : x)))} /> {k}
                    </label>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          {source.gems.length > 0 && (
            <ul className="space-y-1" aria-label="The gems">
              {source.gems.map((g: HoardGem, i) => (
                <li key={`${g.name}#${i}`} className="flex flex-wrap items-center gap-2 text-xs text-stone-300">
                  <span>{g.name}{g.value !== null ? ` (${g.value.toLocaleString()} gp)` : ''}</span>
                  <select className={`${box} w-auto`} aria-label={`Who gets the ${g.name}`} data-testid={`hoard-gem-${i}`} value={gemTo[i] ?? ''}
                    onChange={(e) => setGemTo((was) => was.map((v, j) => (j === i ? e.target.value : v)))}>
                    <option value="">Held</option>
                    {rows.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </li>
              ))}
            </ul>
          )}
          {badNumber ? <p className="text-xs text-amber-300" data-testid="hoard-unfit">Amounts are whole numbers.</p>
            : !fits ? <p className="text-xs text-amber-300" data-testid="hoard-unfit">That gives more than the hoard holds.</p>
              : heldCoins > 0 || gemTo.some((v) => v === '') ? <p className="text-xs text-stone-400" data-testid="hoard-held-note">What isn't given stays held{held ? ` in ${held.name}` : ''}.</p> : null}
          <ul className="text-xs text-stone-500">
            {shares.filter((s) => Object.keys(s.coins).length || s.gems.length).map((s) => <li key={s.characterId}>{nameOf(s.characterId)}: {describeShare(s.coins, s.gems.map((g) => g.name))}</li>)}
          </ul>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={btn} disabled={busy || !fits} data-testid="hoard-confirm" onClick={() => void confirm()}>Confirm</button>
            <button type="button" className={btn} disabled={busy} data-testid="hoard-back" onClick={() => setRows(null)}>Back</button>
          </div>
        </>
      )}
      {error && <p role="alert" className="text-xs text-amber-300" data-testid="hoard-error">{error}</p>}
    </div>
  );
}
